import { z } from 'zod';
import { ColorSchema, SourceRefSchema, about, sourceOf } from '../schema_fields.ts';

// The weather forecast tile (docs/specs/weather.md §4, issue #19): one horizon of a weather
// source's forecast, the next hours, the next days and nights, or the week, as slots in rows. One
// per tile (the owner, on #19: showing all three "makes the tile configuration UI too complicated").

export const FORECAST_HORIZONS = ['short', 'medium', 'long'] as const;
export type ForecastHorizon = (typeof FORECAST_HORIZONS)[number];

/** Rain and wind above the defaults are orange, then red, when the tile sets no colours of its own (the owner, #20). */
const ALERT_ORANGE = '#ffa94d';
const ALERT_RED = '#ff7070';

/** A colour for rain or wind above an amount (WT-9, issue #20), in the forecast's own unit. */
export const ForecastThresholdSchema = z.object({
    above: z.number().min(0).meta(about('Above', 'in the forecast\'s unit: mm or in for rain, kph or mph for wind')),
    color: ColorSchema.meta(about('Colour', undefined, 'color')),
}).meta({ title: 'Threshold' });

export type ForecastThreshold = z.infer<typeof ForecastThresholdSchema>;

export const ForecastOptionsSchema = z.object({
    source: sourceOf(SourceRefSchema, { gives: ['forecast'], label: 'Weather' }),
    horizon: z.enum(FORECAST_HORIZONS).default('medium').meta({
        ...about('Forecast', 'the next hours, the next days and nights, or the week'),
        'x-labels': { short: 'Next hours', medium: 'Next days and nights', long: 'This week' },
    }),
    slots: z.number().int().min(1).max(24).optional().meta(about('Slots', 'how many; 6 hours, 6 days and nights, or 7 days when left out')),
    showRain: z.boolean().default(true).meta(about('Rain', 'in mm, with its chance')),
    showWind: z.boolean().default(true).meta(about('Wind', 'from the calmest to the strongest, with its direction')),
    showWarnings: z.boolean().default(true).meta(about('Warnings', 'a strip at the top, and a mark on the slots they cover')),
    rainColors: z.array(ForecastThresholdSchema).default([{ above: 5, color: ALERT_ORANGE }, { above: 10, color: ALERT_RED }]).meta(about('Rain colours', 'the rain in a colour above an amount; the highest that applies wins')),
    windColors: z.array(ForecastThresholdSchema).default([{ above: 20, color: ALERT_ORANGE }, { above: 40, color: ALERT_RED }]).meta(about('Wind colours', 'the wind in a colour above a speed, by the strongest shown; the highest that applies wins')),
});
